// The ⓘ beside "Client Risk Distribution".
//
// This chart drives action — a therapist who reads "HIGH: 3" will go looking
// for three people — and nothing on the panel says where the number comes
// from. Two things in particular are not guessable from the chart:
//
//   * Half the signal is physiological. A client can land in HIGH because of
//     a blood-oxygen reading, with nothing psychological behind it. Under a
//     heading that says "Risk" in a therapy product, that will be misread.
//   * Clients with no MindVault activity in the selected date range are not
//     on the chart at all — not counted as NORMAL, absent. "HIGH: 0" means
//     "nobody who used the app this week", not "nobody on my caseload".
//
// Every number quoted below is pinned to the code that produces it by
// RiskDistributionInfo.test.js, so a change to the thresholds breaks a test
// instead of quietly turning this panel into a false statement.
import React from 'react';
import { Stack } from '@mui/material';
import DashboardInfoTooltip, { InfoRow, InfoText } from './DashboardInfoTooltip.js';

// Stress-index boundaries, from getSiStatus + isSiCritical/isSiAbnormal in
// mindvaultUtils. Exported so the test can assert they still hold.
export const SI_ABNORMAL_MIN = 200; // Moderate starts here
export const SI_CRITICAL_MIN = 350; // High starts here

// The five vitals getVitalFlagsFromScan actually inspects, in its own order.
export const VITALS_CHECKED = [
  'Heart Rate',
  'Blood Pressure',
  'Blood Oxygen',
  'Respiratory Rate',
  'HRV',
];

export const RISK_RULES = [
  {
    level: 'HIGH',
    color: '#EF4444',
    rule: `a chat flagged high risk, a critical vital sign, or a stress index of ${SI_CRITICAL_MIN}+`,
  },
  {
    level: 'MEDIUM',
    color: '#F59E0B',
    rule: `a chat flagged medium risk, an abnormal vital sign, or a stress index of ${SI_ABNORMAL_MIN}–${SI_CRITICAL_MIN - 1}`,
  },
  {
    level: 'LOW',
    color: '#3B82F6',
    rule: 'a chat flagged low risk, with nothing abnormal in the scan',
  },
  {
    level: 'NORMAL',
    color: '#10B981',
    rule: 'nothing flagged',
  },
];

export default function RiskDistributionInfo() {
  return (
    <DashboardInfoTooltip
      label="How Client Risk Distribution is calculated"
      heading="How this is calculated"
      trackId="dashboard.risk_distribution.info"
    >
      <InfoText sx={{ mb: 1 }}>
        Each client is counted once, at the higher of two signals: their most recent
        MindVault chat and their most recent vitals scan, both taken from the selected
        date range.
      </InfoText>

      <Stack spacing={0.5} sx={{ mb: 1 }}>
        {RISK_RULES.map(({ level, color, rule }) => (
          <InfoRow key={level} term={level} color={color}>{rule}</InfoRow>
        ))}
      </Stack>

      <InfoText sx={{ mb: 1 }}>
        Vital signs checked: {VITALS_CHECKED.join(', ')}.
      </InfoText>

      {/* The line most likely to change what someone concludes from this chart. */}
      <InfoText strong>
        Clients with no MindVault chat or scan in this date range are not counted here.
      </InfoText>
    </DashboardInfoTooltip>
  );
}
